import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: string;
  canonicalUrl?: string;
  structuredData?: Record<string, any>;
  noIndex?: boolean;
  alternateLanguages?: { [key: string]: string };
}

const SEO = ({
  title = "Holionix - Enterprise AI Voice Agents for Customer Service",
  description = "Enterprise-grade AI voice agents for call centers, sales automation, and customer support. Reduce costs by 40% with GDPR-compliant, scalable voice AI technology.",
  keywords = "AI voice agents, call center automation, customer service AI, GDPR compliant voice AI, enterprise voice automation",
  ogImage = "/assets/opengraph-holionix.png",
  ogType = "website",
  canonicalUrl,
  structuredData,
  noIndex = false,
  alternateLanguages,
}: SEOProps) => {
  const location = useLocation();
  const baseUrl = "https://holionix.de";
  const currentUrl = `${baseUrl}${location.pathname}`;

  useEffect(() => {
    // Update title with brand suffix if not already included
    const fullTitle = title.includes("Holionix") ? title : `${title} | Holionix`;
    document.title = fullTitle;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const attribute = property ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`) as HTMLMetaElement;
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      element.content = content;
    };

    // Standard meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);
    
    // Robots meta
    updateMetaTag('robots', noIndex ? 'noindex,nofollow' : 'index,follow');
    
    // Open Graph tags
    updateMetaTag('og:title', fullTitle, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:image', `${baseUrl}${ogImage}`, true);
    updateMetaTag('og:url', canonicalUrl || currentUrl, true);
    updateMetaTag('og:site_name', 'Holionix', true);
    updateMetaTag('og:locale', 'en_US', true);
    updateMetaTag('og:locale:alternate', 'de_DE', true);

    // Twitter Card tags
    updateMetaTag('twitter:card', 'summary_large_image', true);
    updateMetaTag('twitter:site', '@holionix', true);
    updateMetaTag('twitter:title', fullTitle, true);
    updateMetaTag('twitter:description', description, true);
    updateMetaTag('twitter:image', `${baseUrl}${ogImage}`, true);
    updateMetaTag('twitter:creator', '@holionix', true);

    // Mobile optimization
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0, maximum-scale=5.0');
    updateMetaTag('theme-color', '#7928CA');
    updateMetaTag('mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-capable', 'yes');
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'black-translucent');
    updateMetaTag('apple-mobile-web-app-title', 'Holionix');

    // Language alternates
    if (alternateLanguages) {
      Object.entries(alternateLanguages).forEach(([lang, url]) => {
        let link = document.querySelector(`link[hreflang="${lang}"]`) as HTMLLinkElement;
        if (!link) {
          link = document.createElement('link');
          link.rel = 'alternate';
          link.hreflang = lang;
          document.head.appendChild(link);
        }
        link.href = `${baseUrl}${url}`;
      });
    }

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = canonicalUrl || currentUrl;

    // Structured Data
    if (structuredData) {
      let script = document.querySelector('#structured-data') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.id = 'structured-data';
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Holionix",
        "url": baseUrl,
        "logo": `${baseUrl}/assets/logos/holionix-logo.png`,
        ...structuredData
      });
    }

    // Preconnect to critical origins
    const preconnectUrls = [
      'https://fonts.googleapis.com',
      'https://fonts.gstatic.com',
      'https://analytics.holionix.de'
    ];

    preconnectUrls.forEach(url => {
      let link = document.querySelector(`link[href="${url}"]`) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = url;
        document.head.appendChild(link);
      }
    });

  }, [title, description, keywords, ogImage, ogType, canonicalUrl, structuredData, noIndex, alternateLanguages, location.pathname]);

  return null;
};

export default SEO;

// Organization schema example:
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Holionix",
  "url": "https://holionix.de",
  "logo": "https://holionix.de/assets/logos/holionix-logo.png",
  "sameAs": [
    "https://twitter.com/holionix",
    "https://linkedin.com/company/holionix",
    "https://github.com/holionix"
  ],
  "contactPoint": [{
    "@type": "ContactPoint",
    "telephone": "+49 15216724688",
    "contactType": "sales",
    "areaServed": ["DE", "GB", "US"],
    "availableLanguage": ["en", "de"]
  }]
};
